import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { RegionId, TouchpointSnoozeId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Badge } from "components/ui/badge";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useRegionId } from "store/selectors/hooks";
import { agendaSlice } from "store/slices/agenda";
import { ApplicationState } from "store/state";
import { handleEvent } from "util/user-events";
import { contactPath } from "./agenda-actions";
import { describeCadence, describeChannels } from "./marketing-plan-format";

export interface AgendaSnooze {
    snoozeId: string;
    contactId: string;
    contactName: string;
    orgName?: string | null;
    touchpointId?: string | null;
    touchpointKinds?: string[] | null;
    touchpointIntervalUnit?: string | null;
    touchpointIntervalCount?: number | null;
    snoozeUntil: string;
    reason?: string | null;
}

interface AgendaSnoozedSectionProps {
    snoozes: AgendaSnooze[];
    onChanged: () => void;
}

const scopeLabel = (snooze: AgendaSnooze): string => {
    if (
        snooze.touchpointId &&
        snooze.touchpointKinds &&
        snooze.touchpointIntervalCount &&
        snooze.touchpointIntervalUnit
    ) {
        return `${describeChannels(snooze.touchpointKinds)} ${describeCadence(
            snooze.touchpointIntervalCount,
            snooze.touchpointIntervalUnit,
        )}`;
    }
    return "All outreach";
};

const SnoozeRow: React.FC<{ snooze: AgendaSnooze; onChanged: () => void }> = ({
    snooze,
    onChanged,
}) => {
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const unsnoozeMutation = useMutation({
        ...trpcClient.marketingPlaybook.unsnooze.mutationOptions(),
        onSuccess: () => {
            handleEvent("marketing-plan-unsnooze");
            onChanged();
        },
    });

    return (
        <li className="flex flex-col gap-2 px-4 py-3 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 text-sm">
                <Link
                    to={contactPath(snooze.contactId)}
                    className="font-medium text-gray-900 hover:underline">
                    {snooze.contactName}
                </Link>
                <div className="text-gray-500">
                    {scopeLabel(snooze)} · until{" "}
                    {new Date(snooze.snoozeUntil).toLocaleDateString()}
                </div>
                {snooze.reason ? (
                    <div className="text-gray-400 italic">{snooze.reason}</div>
                ) : null}
            </div>
            <SecondaryCompactButton
                onClick={() =>
                    unsnoozeMutation.mutate({
                        regionId: regionId as RegionId,
                        snoozeId: snooze.snoozeId as TouchpointSnoozeId,
                    })
                }
                disabled={unsnoozeMutation.isPending}>
                Un-snooze
            </SecondaryCompactButton>
        </li>
    );
};

export const AgendaSnoozedSection: React.FC<AgendaSnoozedSectionProps> = ({
    snoozes,
    onChanged,
}) => {
    const dispatch = useDispatch();
    const open = useSelector(
        (state: ApplicationState) => state.agenda.sectionVisibility.snoozed,
    );

    if (snoozes.length === 0) {
        return null;
    }

    return (
        <div className="rounded-lg bg-white ring-1 ring-gray-300">
            <button
                type="button"
                onClick={() =>
                    dispatch(agendaSlice.actions.toggleAgendaSection("snoozed"))
                }
                className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-50">
                <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                        open ? "rotate-0" : "-rotate-90"
                    }`}
                    aria-hidden="true"
                />
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    Snoozed
                </span>
                <Badge value={snoozes.length} type="info" />
            </button>
            {open ? (
                <ul className="divide-y divide-gray-100 border-t border-gray-100">
                    {snoozes.map(snooze => (
                        <SnoozeRow
                            key={snooze.snoozeId}
                            snooze={snooze}
                            onChanged={onChanged}
                        />
                    ))}
                </ul>
            ) : null}
        </div>
    );
};
